import { Injectable, inject, signal } from '@angular/core';
import { CommunicationSocketService } from './communication-socket.service';

export type ChatGoalKind = 'lexicon' | 'grammar' | 'subject';

export interface ChatGoal {
  id: string;
  kind: ChatGoalKind;
  /** Lexicon category, grammar topic, or subject name. */
  label: string;
  /** Target count for lexicon and grammar goals. */
  count?: number;
}

export interface ChatGoalView extends ChatGoal {
  title: string;
  current: number;
  target: number;
  progress: number;
  accent: string;
}

const STORAGE_PREFIX = 'brandon.chat-goals.v1';
export const CHAT_GOALS_MAX = 4;

const GOAL_ACCENTS = [
  'var(--color-ocean-blue)',
  'var(--color-warm-500)',
  'var(--color-sea-green)',
  'var(--color-coral)',
] as const;

export function chatGoalsStorageKey(studentUserId: string): string {
  return `${STORAGE_PREFIX}:${studentUserId}`;
}

export function readChatGoals(studentUserId: string): ChatGoal[] {
  if (!studentUserId || typeof localStorage === 'undefined') {
    return [];
  }
  try {
    const raw = localStorage.getItem(chatGoalsStorageKey(studentUserId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ChatGoal[];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (goal) =>
          goal &&
          typeof goal.id === 'string' &&
          (goal.kind === 'lexicon' || goal.kind === 'grammar' || goal.kind === 'subject') &&
          typeof goal.label === 'string' &&
          goal.label.trim().length > 0,
      )
      .slice(0, CHAT_GOALS_MAX);
  } catch {
    return [];
  }
}

export function writeChatGoals(studentUserId: string, goals: ChatGoal[]): void {
  if (!studentUserId || typeof localStorage === 'undefined') {
    return;
  }
  localStorage.setItem(
    chatGoalsStorageKey(studentUserId),
    JSON.stringify(goals.slice(0, CHAT_GOALS_MAX)),
  );
}

export function goalKindTitle(kind: ChatGoalKind): string {
  switch (kind) {
    case 'lexicon':
      return 'Lexicon target';
    case 'grammar':
      return 'Grammar';
    case 'subject':
      return 'Subject';
  }
}

export function buildGoalViews(
  goals: readonly ChatGoal[],
  progress: Readonly<Record<string, number>>,
): ChatGoalView[] {
  return goals.map((goal, index) => {
    const target = goal.kind === 'subject' ? 0 : Math.max(1, goal.count ?? 1);
    const current = progress[goal.id] ?? 0;
    const progressPct =
      goal.kind === 'subject' ? 0 : Math.min(100, Math.round((current / target) * 100));
    return {
      ...goal,
      title: goalKindTitle(goal.kind),
      current,
      target,
      progress: progressPct,
      accent: GOAL_ACCENTS[index % GOAL_ACCENTS.length],
    };
  });
}

@Injectable({ providedIn: 'root' })
export class ChatGoalsService {
  private readonly socket = inject(CommunicationSocketService);
  private readonly cache = signal<Record<string, ChatGoal[]>>({});
  private readonly revision = signal(0);

  /** Bumps whenever goals change locally or from the chat socket. */
  readonly goalsRevision = this.revision.asReadonly();

  goalsFor(studentUserId: string): ChatGoal[] {
    this.revision();
    if (!studentUserId) return [];
    const cached = this.cache()[studentUserId];
    if (cached !== undefined) return cached;
    return readChatGoals(studentUserId);
  }

  ensureLoaded(studentUserId: string): void {
    if (!studentUserId || this.cache()[studentUserId] !== undefined) {
      return;
    }
    this.cache.update((all) => ({
      ...all,
      [studentUserId]: readChatGoals(studentUserId),
    }));
  }

  setGoals(
    studentUserId: string,
    goals: ChatGoal[],
    conversationId?: string | null,
  ): void {
    if (!studentUserId) return;
    const next = goals.slice(0, CHAT_GOALS_MAX);
    writeChatGoals(studentUserId, next);
    this.cache.update((all) => ({ ...all, [studentUserId]: next }));
    this.revision.update((value) => value + 1);
    if (conversationId) {
      this.socket.syncGoals(conversationId, studentUserId, next);
    }
  }

  applyRemoteGoals(studentUserId: string, goals: ChatGoal[]): void {
    if (!studentUserId) return;
    const next = goals.slice(0, CHAT_GOALS_MAX);
    writeChatGoals(studentUserId, next);
    this.cache.update((all) => ({ ...all, [studentUserId]: next }));
    this.revision.update((value) => value + 1);
  }

  addGoal(
    studentUserId: string,
    goal: Omit<ChatGoal, 'id'>,
    conversationId?: string | null,
  ): ChatGoal | null {
    const current = this.goalsFor(studentUserId);
    if (current.length >= CHAT_GOALS_MAX) return null;
    const next: ChatGoal = {
      ...goal,
      id:
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `goal-${Date.now()}`,
      label: goal.label.trim(),
      count: goal.kind === 'subject' ? undefined : Math.max(1, goal.count ?? 1),
    };
    this.setGoals(studentUserId, [...current, next], conversationId);
    return next;
  }

  removeGoal(
    studentUserId: string,
    goalId: string,
    conversationId?: string | null,
  ): void {
    const current = this.goalsFor(studentUserId);
    this.setGoals(
      studentUserId,
      current.filter((goal) => goal.id !== goalId),
      conversationId,
    );
  }
}
